import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
	createFileRoute,
	Outlet,
	useNavigate,
	useParams,
} from "@tanstack/react-router";
import { Building01 } from "@untitledui/icons";
import { useEffect, useMemo, useRef, useState } from "react";
import { z } from "zod";
import { Avatar } from "@/components/base/avatar/avatar";
import { BadgeWithDot } from "@/components/base/badges/badges";
import { Button } from "@/components/base/buttons/button";
import {
	EmptyValue,
	PhoneNumber,
	TableAddressDisplay,
} from "@/components/common";
import CopyableId from "@/components/Molecules/CopyableId/CopyableId";
import {
	SubOrgTableFilters,
	type SubOrgTableFiltersState,
} from "@/components/Molecules/FilterDrawer/SubOrgTableFilters";
import type { ColumnDef } from "@/components/Molecules/Table";
import { CustomTableWrapper } from "@/components/Molecules/Table/CustomTableWrapper";
import { TableSearchEmptyState } from "@/components/Molecules/Table/TableSearchEmptyState";
import CreateSubOrgModal from "@/components/Organisms/CreateSubOrgModal/CreateSubOrgModal";
import { PageHeader } from "@/components/Organisms/PageHeader/PageHeader";
import { PageLayout } from "@/components/Organisms/PageLayout/PageLayout";
import { Illustration } from "@/components/shared-assets/illustrations";
import { useAppTranslation } from "@/hooks/use-app-translation";
import { useEntityActionAllowed } from "@/hooks/use-entity-action-allowed";
import { useDebounced } from "@/hooks/use-debounced";
import { useTableSettings } from "@/hooks/use-table-settings";
import {
	organizationQueryOptions,
	type SubOrganizationDisplay,
	useExportSubOrganizations,
} from "@/services/organization";
import {
	isClosedSubOrganization,
	subOrganizationStatusesParam,
	SUB_ORGANIZATION_STATUSES_INCLUDING_CLOSED,
	SubOrganizationStatusFilter,
} from "@/services/organization/organization.constants";
import {
	buildFilterParams,
	type FilterConfig,
} from "@/utils/build-filter-params";
import { downloadBlob, generateExportFilename } from "@/utils/download";
import { formatLongDate } from "@/utils/utils";

// Table settings constants
const TABLE_KEY = "orgAdmin.subOrganizations";
const DEFAULT_COLUMN_ORDER = [
	"name",
	"status",
	"location",
	"phone",
	"totalAssignedUserCount",
	"usersWithFullDataAccessCount",
	"usersWithLimitedDataAccessCount",
	"created",
	"lastUpdate",
];
const DEFAULT_SORTING = [{ id: "name", desc: false }];
const DEFAULT_PAGE_SIZE = 10;

const normalizeColumnOrder = (columnOrder?: string[]) => {
	if (!Array.isArray(columnOrder) || columnOrder.length === 0) {
		return [...DEFAULT_COLUMN_ORDER];
	}

	const allowedColumns = new Set(DEFAULT_COLUMN_ORDER);
	const sanitized = columnOrder.filter((columnId) =>
		allowedColumns.has(columnId),
	);
	const unique = Array.from(new Set(sanitized));
	const missingDefaults = DEFAULT_COLUMN_ORDER.filter(
		(columnId) => !unique.includes(columnId),
	);

	return [...unique, ...missingDefaults];
};

// Serializable filter state for persistence (excludes date ranges which are complex objects)
// Note: Date ranges are not persisted - they reset on page load
type PersistedFilters = Record<string, never>;
const DEFAULT_FILTERS: PersistedFilters = {};

export const Route = createFileRoute(
	"/_authenticated/(org-app)/_orgAdmin/org-admin/$orgId/sub-organizations",
)({
	component: RouteComponent,
	validateSearch: (search) => {
		return z
			.object({
				mode: z.enum(["view", "edit"]).optional(),
				tab: z.enum(["details", "users", "data-access"]).optional(),
				organizationId: z.string().optional(),
				subOrganizationId: z.string().optional(),
				filterOpen: z.boolean().optional(),
				create: z.enum(["sub-organization"]).optional(),
			})
			.parse(search);
	},
});

function RouteComponent() {
	const { t } = useAppTranslation();
	const navigate = useNavigate();
	const params = useParams({
		from: "/_authenticated/(org-app)/_orgAdmin/org-admin/$orgId/sub-organizations",
	});
	const [isOpen, setIsOpen] = useState(false);
	const [tableOptionsHost, setTableOptionsHost] =
		useState<HTMLDivElement | null>(null);
	const canCreateSubOrganization = useEntityActionAllowed({
		name: "CreateSubOrganization",
		enabled: params.orgId.length > 0,
	});
	const openCreate = () => {
		setIsOpen(true);
		navigate({
			to: "/org-admin/$orgId/sub-organizations",
			params: { orgId: params.orgId },
			search: { create: "sub-organization" } as Record<string, unknown>,
		});
	};

	const organizationName = useQuery({
		...organizationQueryOptions.detail(params.orgId),
		select: (data) => data?.name,
	})?.data;

	return (
		<>
			<PageLayout
				header={
					<PageHeader
						type="landing"
						title={`${t("subOrganization.entityLabel")}s`}
						description={t("org-admin.sub-organizations.table.description")}
						actions={
							canCreateSubOrganization.allowed
								? [
										{
											label: t(
												"org-admin.sub-organizations.table.createSubOrganization",
											),
											color: "accent" as const,
											onPress: openCreate,
										},
									]
								: undefined
						}
						overflowControl={<div ref={setTableOptionsHost} />}
						overflowAriaLabel={t("common.tableActions.options")}
					/>
				}
			>
				<SubOrganizationsTableComponent
					tableOptionsPortalTarget={tableOptionsHost}
					onCreateClick={
						canCreateSubOrganization.allowed ? openCreate : undefined
					}
				/>
			</PageLayout>
			{canCreateSubOrganization.allowed ? (
			<CreateSubOrgModal
				isOpen={isOpen}
				setIsOpen={(o) => {
					setIsOpen(o);
					// Update URL when closing
					if (!o) {
						navigate({
							to: "/org-admin/$orgId/sub-organizations",
							params: { orgId: params.orgId },
							search: {},
						});
					}
				}}
				handleClose={() => {
					setIsOpen(false);
					navigate({
						to: "/org-admin/$orgId/sub-organizations",
						params: { orgId: params.orgId },
						search: {},
					});
				}}
				organizationName={organizationName ?? ""}
			/>
			) : null}
		</>
	);
}

function SubOrganizationsTableComponent({
	onCreateClick,
	tableOptionsPortalTarget = null,
}: {
	onCreateClick?: () => void;
	tableOptionsPortalTarget?: HTMLElement | null;
}) {
	const navigate = useNavigate();
	const { t } = useAppTranslation();
	const queryClient = useQueryClient();
	const subOrgLabel = t("subOrganization.entityLabel");
	const params = useParams({
		from: "/_authenticated/(org-app)/_orgAdmin/org-admin/$orgId/sub-organizations",
	});

	// Pagination state
	const [pageSize, setPageSize] = useState(10);
	const [pageIndex, setPageIndex] = useState(0);
	const [searchTerm, setSearchTerm] = useState("");
	const [isRefreshing, setIsRefreshing] = useState(false);
	const [filters, setFilters] = useState<SubOrgTableFiltersState>({
		status: SubOrganizationStatusFilter.Active,
		createdDateRange: undefined,
		lastUpdateDateRange: undefined,
	});
	const [sortParams, setSortParams] = useState({
		"OrderBy.Fields": ["Name"],
		"OrderBy.Descending": false,
	});

	// Track if persisted settings have been applied
	const hasAppliedPersistedSettings = useRef(false);

	// Table settings persistence
	const {
		settings: tableSettings,
		isLoaded: settingsLoaded,
		saveSorting,
		saveColumnOrder,
		saveColumnVisibility,
		saveSearchTerm,
		saveFilters,
		savePageSize,
	} = useTableSettings<PersistedFilters>({
		tableKey: TABLE_KEY,
		defaultSorting: DEFAULT_SORTING,
		defaultColumnOrder: DEFAULT_COLUMN_ORDER,
		defaultSearchTerm: "",
		defaultFilters: DEFAULT_FILTERS,
		defaultPageSize: DEFAULT_PAGE_SIZE,
	});

	// Apply persisted settings (search, filters, page size) when config has loaded
	useEffect(() => {
		if (!settingsLoaded || hasAppliedPersistedSettings.current) return;
		hasAppliedPersistedSettings.current = true;

		if (tableSettings.searchTerm) {
			setSearchTerm(tableSettings.searchTerm);
		}
		if (tableSettings.filters) {
			setFilters((prev) => ({
				...prev,
			}));
		}
		if (
			Number.isFinite(tableSettings.pageSize) &&
			[10, 25, 50, 100].includes(tableSettings.pageSize)
		) {
			setPageSize(tableSettings.pageSize);
		}
	}, [
		settingsLoaded,
		tableSettings.searchTerm,
		tableSettings.filters,
		tableSettings.pageSize,
	]);

	// Sort field mapping - maps column IDs to API field names
	const sortFieldMapping: Record<string, string[]> = {
		name: ["Name"],
		address: ["Address.City", "Address.State"],
		phone: ["Phone"],
		totalAssignedUserCount: ["TotalAssignedUserCount"],
		usersWithFullDataAccessCount: ["UsersWithFullDataAccessCount"],
		usersWithLimitedDataAccessCount: ["UsersWithLimitedDataAccessCount"],
		created: ["CreatedAt"],
		lastUpdated: ["UpdatedAt"],
		id: ["Id"],
	};

	// Calculate the start index for the query (1-based indexing)
	const startIndex = pageIndex * pageSize + 1;
	const exportMutation = useExportSubOrganizations(params.orgId);
	const debouncedFilters = useDebounced(filters, 500);

	// Filter configuration for buildFilterParams utility
	const filterConfig: FilterConfig = {
		"createdDateRange.start": {
			paramName: "CreatedAfter",
			transform: "dateStart",
		},
		"createdDateRange.end": {
			paramName: "CreatedBefore",
			transform: "dateEnd",
			dateStartField: "createdDateRange.start",
		},
		"lastUpdateDateRange.start": {
			paramName: "LastUpdatedAfter",
			transform: "dateStart",
		},
		"lastUpdateDateRange.end": {
			paramName: "LastUpdatedBefore",
			transform: "dateEnd",
			dateStartField: "lastUpdateDateRange.start",
		},
	};

	const filterParams = useMemo(
		() => buildFilterParams(debouncedFilters, filterConfig),
		[debouncedFilters],
	);
	const statusFilter =
		debouncedFilters.status ?? SubOrganizationStatusFilter.Active;
	const statusParams = useMemo(
		() => ({
			Statuses: subOrganizationStatusesParam(statusFilter),
		}),
		[statusFilter],
	);

	// Get sub-organization count
	const _subOrganizationCount = useQuery(
		organizationQueryOptions.subOrganizations.count(params.orgId, {
			SearchTerm: searchTerm,
			...filterParams,
			...statusParams,
			...sortParams,
		}),
	);

	const unFilteredSubOrganizationCount = useQuery(
		organizationQueryOptions.subOrganizations.count(params.orgId, {
			Statuses: SUB_ORGANIZATION_STATUSES_INCLUDING_CLOSED,
		}),
	);

	// Fetch sub-organizations with regular query, using pagination
	const allSubOrganizations = useQuery(
		organizationQueryOptions.subOrganizations.list(
			params.orgId,
			startIndex,
			pageSize,
			{
				SearchTerm: searchTerm,
				...filterParams,
				...statusParams,
				...sortParams,
			},
		),
	);

	// Use the data directly from the query
	const data = useMemo(() => {
		return allSubOrganizations.data ?? [];
	}, [allSubOrganizations.data]);

	// Calculate total page count from total sub-organization count
	const pageCount = useMemo(() => {
		if (!_subOrganizationCount.data) return 0;
		return Math.ceil(_subOrganizationCount.data / pageSize);
	}, [_subOrganizationCount.data, pageSize]);

	// Handle refresh with loading state
	const handleRefresh = async () => {
		setIsRefreshing(true);
		try {
			await allSubOrganizations.refetch();
		} finally {
			setIsRefreshing(false);
		}
	};

	const handleExport = (visibleColumns?: string[]) => {
		const totalCount = _subOrganizationCount.data ?? 0;
		if (totalCount === 0 || totalCount > 10000) return;

		const mappedColumns = visibleColumns?.flatMap((col) => {
			if (col === "address") {
				return [
					"address.street1",
					"address.street2",
					"address.city",
					"address.state",
					"address.country",
					"address.postalCode",
				];
			}
			return col;
		});

		exportMutation.mutate(
			{
				startIndex: 1,
				count: totalCount,
				params: {
					SearchTerm: searchTerm,
					...filterParams,
					...statusParams,
					...sortParams,
					columns: mappedColumns?.join(","),
				},
			},
			{
				onSuccess: (blob) => {
					downloadBlob(blob, generateExportFilename("sub_organizations"));
				},
			},
		);
	};

	// Handle row click to navigate to sub-organization details
	const handleRowClick = (subOrganization: SubOrganizationDisplay) => {
		if (!subOrganization.id) return;
		navigate({
			to: "/org-admin/$orgId/sub-organizations/$subOrgId/details",
			params: { orgId: params.orgId, subOrgId: subOrganization.id },
			search: {
				mode: "view",
				organizationId: params.orgId,
				subOrganizationId: subOrganization.id,
				tab: "details",
			},
		});
	};

	// Handler for pagination changes from the table
	const handlePaginationChange = ({
		pageSize: newPageSize,
		pageIndex: newPageIndex,
	}: {
		pageSize: number;
		pageIndex: number;
	}) => {
		if (newPageSize !== pageSize) {
			setPageSize(newPageSize);
			setPageIndex(0);
			savePageSize(newPageSize);
		} else if (newPageIndex !== pageIndex) {
			setPageIndex(newPageIndex);
		}
	};

	const handleDebouncedSearchChange = (search: string) => {
		setSearchTerm(search);
		setPageIndex(0);
		// Save search term for persistence
		saveSearchTerm(search);
	};

	const handleFilterChange = (newFilters: SubOrgTableFiltersState) => {
		setFilters(newFilters);
		setPageIndex(0);
		// Save filters for persistence (only serializable fields)
		// Note: Date ranges are not persisted
		saveFilters({});
	};

	// Column definitions
	const subOrganizationColumns: ColumnDef<SubOrganizationDisplay>[] = [
		{
			id: "name",
			accessorKey: "name",
			header: t("org-admin.sub-organizations.table.columns.name"),
			enableSorting: true,
			meta: {
				headerProps: {
					isRowHeader: true,
				},
			},
			minSize: 350,
			cell: ({ row }) => {
				const subOrganization = row.original;
				return (
					<span className="text-sm flex items-center">
						<Avatar
							className="mr-3 [&_svg]:text-fg-white"
							size="sm"
							placeholderIcon={Building01}
						/>
						<div className="flex flex-col">
							{subOrganization.name ? (
								`${subOrganization.name}`
							) : (
								<EmptyValue />
							)}
						</div>
					</span>
				);
			},
		},
		{
			id: "status",
			accessorKey: "statusCode",
			header: t("org-admin.sub-organizations.table.columns.status"),
			enableSorting: false,
			minSize: 140,
			cell: ({ row }) => {
				const closed = isClosedSubOrganization(row.original.statusCode);
				return (
					<BadgeWithDot
						type="pill-color"
						size="sm"
						color={closed ? "gray" : "success"}
					>
						{closed
							? t("org-admin.sub-organizations.status.closed")
							: t("org-admin.sub-organizations.status.active")}
					</BadgeWithDot>
				);
			},
		},
		{
			id: "address",
			accessorKey: "address",
			header: t("org-admin.sub-organizations.table.columns.location"),
			enableSorting: true,
			minSize: 300,
			cell: ({ row }) => {
				return row.original.address ? (
					<TableAddressDisplay address={row.original.address} />
				) : (
					<EmptyValue />
				);
			},
		},
		{
			id: "phone",
			accessorKey: "phone",
			header: t("org-admin.sub-organizations.table.columns.phone"),
			enableSorting: true,
			minSize: 188,
			cell: ({ row }) => {
				return row.original.phone ? (
					<PhoneNumber phoneNumber={row.original.phone} />
				) : (
					<EmptyValue />
				);
			},
		},
		{
			id: "totalAssignedUserCount",
			accessorKey: "totalAssignedUserCount",
			header: t("org-admin.sub-organizations.table.columns.userCount"),
			enableSorting: true,
			minSize: 78,
			cell: ({ row }) => {
				return row.original.totalAssignedUserCount !== undefined &&
					row.original.totalAssignedUserCount !== null ? (
					<span className="text-sm font-medium">
						{row.original.totalAssignedUserCount}
					</span>
				) : (
					<EmptyValue />
				);
			},
		},
		{
			id: "usersWithFullDataAccessCount",
			accessorKey: "usersWithFullDataAccessCount",
			header: t("org-admin.sub-organizations.table.columns.allDataUserCount"),
			enableSorting: true,
			minSize: 75,
			cell: ({ row }) => {
				return row.original.usersWithFullDataAccessCount !== undefined &&
					row.original.usersWithFullDataAccessCount !== null ? (
					<span className="text-sm font-medium">
						{row.original.usersWithFullDataAccessCount}
					</span>
				) : (
					<EmptyValue />
				);
			},
		},
		{
			id: "usersWithLimitedDataAccessCount",
			accessorKey: "usersWithLimitedDataAccessCount",
			header: t(
				"org-admin.sub-organizations.table.columns.assignedDataUserCount",
			),
			enableSorting: true,
			minSize: 85,
			cell: ({ row }) => {
				return row.original.usersWithLimitedDataAccessCount !== undefined &&
					row.original.usersWithLimitedDataAccessCount !== null ? (
					<span className="text-sm font-medium">
						{row.original.usersWithLimitedDataAccessCount}
					</span>
				) : (
					<EmptyValue />
				);
			},
		},
		{
			id: "created",
			accessorKey: "created",
			header: t("org-admin.sub-organizations.table.columns.created"),
			enableSorting: true,
			minSize: 175,
			cell: ({ row }) => {
				return row.original.created ? (
					<span className="text-sm font-medium">
						{formatLongDate(row.original.created)}
					</span>
				) : (
					<EmptyValue />
				);
			},
		},
		{
			id: "lastUpdated",
			accessorKey: "lastUpdated",
			header: t("org-admin.sub-organizations.table.columns.lastUpdate"),
			enableSorting: true,
			minSize: 175,
			cell: ({ row }) => {
				return row.original.lastUpdated ? (
					<span className="text-sm font-medium">
						{formatLongDate(row.original.lastUpdated)}
					</span>
				) : (
					<EmptyValue />
				);
			},
		},
		{
			id: "id",
			accessorKey: "id",
			header: t("org-admin.sub-organizations.table.columns.id"),
			enableSorting: true,
			cell: ({ row }) => {
				return row.original.id ? (
					<div className="inline-flex min-w-0 max-w-full">
						<span className="text-sm font-medium">
							<CopyableId value={row.original.id} />
						</span>
					</div>
				) : (
					<EmptyValue />
				);
			},
		},
	];

	const noSubOrganizations = (isSearchEmpty: boolean) => {
		if (isSearchEmpty) {
			return (
				<TableSearchEmptyState titleKey="org-admin.sub-organizations.table.empty.noSearchResults" />
			);
		}

		return (
			<div className="flex flex-col items-center justify-center">
				<div className="relative flex items-center justify-center w-[480px] h-[480px]">
					<div className="relative flex flex-col items-center justify-center">
						<Illustration type="cloud" size="sm">
							<Building01 className="size-6" />
						</Illustration>
						<div className="mt-4 text-center">
							<h2 className="font-semibold text-primary text-md">
								{t("org-admin.sub-organizations.table.empty.title")}
							</h2>
							<p className="text-sm text-tertiary">
								{t("org-admin.sub-organizations.table.empty.description")}
							</p>
						</div>
						{onCreateClick ? (
							<Button
								size="sm"
								color="primary"
								onClick={onCreateClick}
								className="mt-4"
							>
								{t("org-admin.sub-organizations.table.createSubOrganization")}
							</Button>
						) : null}
					</div>
				</div>
			</div>
		);
	};

	// Prefetch adjacent pages to eliminate loading delays
	useEffect(() => {
		// Don't prefetch if we don't have a page count yet
		if (!pageCount) return;

		// Configure how many pages ahead/behind to prefetch
		const prefetchRange = 1;

		// Prefetch multiple pages ahead
		for (let i = 1; i <= prefetchRange; i++) {
			const nextPageIndex = pageIndex + i;
			if (nextPageIndex < pageCount) {
				const nextStartIndex = nextPageIndex * pageSize + 1;
				queryClient.prefetchQuery(
					organizationQueryOptions.subOrganizations.list(
						params.orgId,
						nextStartIndex,
						pageSize,
						{
							SearchTerm: searchTerm,
							...filterParams,
							...statusParams,
							...sortParams,
						},
					),
				);
			}
		}

		// Prefetch multiple pages behind
		for (let i = 1; i <= prefetchRange; i++) {
			const prevPageIndex = pageIndex - i;
			if (prevPageIndex >= 0) {
				const prevStartIndex = prevPageIndex * pageSize + 1;
				queryClient.prefetchQuery(
					organizationQueryOptions.subOrganizations.list(
						params.orgId,
						prevStartIndex,
						pageSize,
						{
							SearchTerm: searchTerm,
							...filterParams,
							...statusParams,
							...sortParams,
						},
					),
				);
			}
		}
	}, [
		pageIndex,
		pageSize,
		pageCount,
		searchTerm,
		filterParams,
		statusParams,
		sortParams,
		queryClient,
		params.orgId,
	]);

	// Handle error state
	if (allSubOrganizations.isError) {
		return (
			<div className="space-y-0">
				<div className="flex flex-col items-center justify-center py-12">
					<div className="text-center">
						<h3 className="text-sm font-medium text-error">
							{t(
								"org-admin.sub-organizations.table.errors.failedToLoadSubOrganizations",
							)}
						</h3>
						<p className="text-sm text-tertiary mt-1">
							{allSubOrganizations.error?.message}
						</p>
						<Button
							size="sm"
							color="primary"
							onClick={handleRefresh}
							className="mt-3"
						>
							{t("common.actions.tryAgain")}
						</Button>
					</div>
				</div>
			</div>
		);
	}

	return (
		<div className="space-y-0">
			<CustomTableWrapper
				tableKey={TABLE_KEY}
				// Data
				data={data}
				columns={subOrganizationColumns}
				// Display
				title={t("org-admin.sub-organizations.table.title")}
				showHeaderPadding={false}
				showCard={true}
				zebraStriping
				// Loading States
				isLoading={allSubOrganizations.isLoading}
				isFetching={allSubOrganizations.isFetching}
				isRefreshing={isRefreshing}
				onRefresh={handleRefresh}
				// Pagination
				paginationMode="server"
				externalPaginationState={{ pageSize, pageIndex }}
				onPaginationChange={handlePaginationChange}
				pageCount={pageCount}
				rowCount={_subOrganizationCount.data}
				totalRowCount={unFilteredSubOrganizationCount.data}
				// Sorting
				sortFieldMapping={sortFieldMapping}
				defaultSortField="name"
				onSortChange={({ fields, descending }) => {
					setSortParams({
						"OrderBy.Fields": fields,
						"OrderBy.Descending": descending,
					});
				}}
				// Filtering
				filterPlaceholder={t(
					"org-admin.sub-organizations.table.search.placeholder",
					{ subOrganization: subOrgLabel },
				)}
				filterValue={searchTerm}
				onDebouncedFilterChange={handleDebouncedSearchChange}
				filtersTrailingContent={
					<SubOrgTableFilters value={filters} onChange={handleFilterChange} />
				}
				// Table Actions
				enableTableActions
				filterLayout="bar"
				filterBarType="landing"
				filterBarClassName="pt-0"
				tableOptionsPortalTarget={tableOptionsPortalTarget}
				hasActiveFilters={
					Object.keys(filterParams).length > 0 ||
					statusFilter !== SubOrganizationStatusFilter.Active
				}
				onExportClick={handleExport}
				isExportLoading={exportMutation.isPending}
				// Features
				enableColumnReordering
				onRowClick={handleRowClick}
				enableColumnResizing
				// Persistence - Initial values from saved settings
				initialSortingState={tableSettings.sorting}
				initialColumnOrder={normalizeColumnOrder(tableSettings.columnOrder)}
				initialColumnVisibility={tableSettings.columnVisibility}
				settingsLoaded={settingsLoaded}
				// Persistence - Save callbacks
				onSortingStateChange={saveSorting}
				onColumnOrderChange={(order) => {
					saveColumnOrder(normalizeColumnOrder(order));
				}}
				onColumnVisibilityChange={saveColumnVisibility}
			/>

			{!allSubOrganizations.isLoading &&
			!allSubOrganizations.isFetching &&
			data?.length <= 0
				? noSubOrganizations(
						(unFilteredSubOrganizationCount.data ?? 0) > 0 &&
							(_subOrganizationCount.data ?? 0) === 0,
					)
				: null}
			<Outlet />
		</div>
	);
}
